import type { RelatedTaskLinkProps } from './navigation';
import type {
  RelatedTaskItem,
  RelatedTaskItemRelationship,
  SiblingTaskItem,
  TaskRelationViewRelationType,
  TaskStatus,
  TaskView,
} from '@/lib/api/generated/model';

/**
 * One row in the related-tasks dialog, whichever endpoint it came from.
 * Siblings carry no structural relationship or depth — they are not an
 * ancestor or descendant of the current task, just another task the same
 * step produced — so those fields are absent rather than faked.
 */
export type RelatedTaskEntry =
  | {
      kind: 'related';
      taskId: string;
      title: string;
      stepName: string;
      status: TaskStatus;
      relationship: RelatedTaskItemRelationship;
      depth: number;
      // The nearest-path relation's semantic type (FR-043) — independent of
      // `relationship`, which describes graph position, not meaning.
      relationType: TaskRelationViewRelationType;
      // The task this one connects through, one hop nearer the selected task
      // — `null` at depth 1, where the selected task itself is that next hop.
      // Lets a graph chain edges instead of connecting every related task
      // straight back to the selected task regardless of depth.
      viaTaskId: string | null;
    }
  | {
      kind: 'sibling';
      taskId: string;
      title: string;
      stepName: string;
      status: TaskStatus;
    };

export function toRelatedTaskEntry(
  item: RelatedTaskItem | SiblingTaskItem,
): RelatedTaskEntry {
  if ('relationship' in item) {
    return {
      kind: 'related',
      taskId: item.task_id,
      title: item.title,
      stepName: item.step_name,
      status: item.status,
      relationship: item.relationship,
      depth: item.depth,
      relationType: item.relation_type,
      viaTaskId: item.via_task_id ?? null,
    };
  }
  return {
    kind: 'sibling',
    taskId: item.task_id,
    title: item.title,
    stepName: item.step_name,
    status: item.status,
  };
}

/**
 * One row in the shared Relations Section widget (068), used identically by
 * the creation form's staged list and a task's persisted direct relations.
 * `relationId` is `null` for a staged entry that has no backend relation yet
 * — only a persisted entry (loaded via `useDirectTaskRelations`, or returned
 * by a successful add) carries the real edge id needed to remove it.
 */
export interface RelationEntry {
  relationId: string | null;
  /**
   * Only `id`/`title`/`workflow_id` are read anywhere this is consumed —
   * kept narrow (rather than the full `TaskView`) so a relation whose target
   * isn't in the loaded workspace aggregate can still render as a row/node
   * instead of being dropped (see `TaskDetailsPanel`).
   */
  relatedTask: Pick<TaskView, 'id' | 'title' | 'workflow_id'>;
  relationType: TaskRelationViewRelationType;
  /**
   * Direction relative to the task the section is mounted on: which side of
   * `parent_task_id`/`child_task_id` the *current* task occupies. Needed by
   * `relationsToGraph` to orient the edge without re-deriving it from raw
   * ids.
   */
  direction: 'parent' | 'child';
}

const RELATION_TYPE_LABEL: Record<TaskRelationViewRelationType, string> = {
  plain: 'Related',
  blocks: 'Blocks',
  relates_to: 'Relates to',
  duplicates: 'Duplicates',
};

/**
 * How a relationship is named, everywhere it is shown (spec 100 FR-038).
 *
 * There were two of these. One derived the name from which side of the edge the
 * *current* task sat on and inverted it; the other trusted a field the server had
 * already oriented towards the related task. They agreed, but by luck rather than
 * by rule, and a reader had no way to know which surface they were looking at.
 *
 * `plain` is named "Related" rather than "Parent" or "Child" (FR-039). A plain
 * connection is what you get when nobody chose a kind, so naming it after a
 * hierarchy asserts something the person never said. Distance and path, which is
 * what a reader actually wants from a multi-hop row, are carried by the row
 * itself rather than smuggled into this label.
 */
export function relationLabel(
  relationType: TaskRelationViewRelationType,
): string {
  return RELATION_TYPE_LABEL[relationType];
}

/**
 * One row, whichever group it sits in (see `data-model.md`).
 *
 * `onRemove` is non-null only for one of the current task's own connections. A
 * task reached through another task carries no relation edge id in its payload —
 * the connection being shown is not this task's to remove — so the type makes a
 * removable row impossible to build without one, rather than leaving a component
 * to remember the rule.
 */
export interface RelationRowModel {
  /** Stable list key: the related task's id, or the relation's where one exists. */
  key: string;
  title: string;
  /**
   * Where the related task sits, in the words the caller has. The tab's wider
   * group has a server-provided step name; the creation form has the workflow a
   * staged task belongs to, which is what tells someone they are reaching across
   * workflows. One slot rather than two, because a row shows one line of context
   * and which kind it is never changes what the reader does with it.
   */
  context: string | null;
  /**
   * Deliberately absent.
   *
   * Spec FR-010 asks each row to state the related task's execution state, and
   * both payloads carry a `status` that no component has ever rendered. Reading
   * the contract during implementation explains why that was harmless: `TaskStatus`
   * has exactly one member, `active`, so the field is a constant. Rendering it
   * would print "active" on every row and answer nothing. The state a reader
   * actually wants — running, failed, waiting — is derived from a task's
   * executions, which neither relations payload carries. Supplying it needs a
   * backend change, which this feature has ruled out of scope.
   */
  /** From {@link relationLabel}, or "Sibling". */
  label: string;
  /** Router link props, or null when the workflow cannot be resolved. */
  link: RelatedTaskLinkProps | null;
  /** Hops away, for a task reached through another task. */
  distance: number | null;
  /** The task it is reached through, when that task is known. */
  viaTitle: string | null;
  onRemove: (() => void) | null;
}
